import React from "react";
import { Typography } from "..";
import { ImpactType, pluralise, User } from "@ecologi/shared";
import { DarkLinkText } from "./HowCalculatedModal";

export const EXPLAINERS: Record<
  Exclude<ImpactType, ImpactType.TREES>,
  () => JSX.Element
> = {
  [ImpactType.CARBON_OFFSETS]: () => (
    <>
      <Typography use="body1">
        While the biggest impact you can make is to reduce your own emissions,
        carbon avoidance projects all provide complementary climate solutions to
        prevent greenhouse gas emissions, restore nature, and support local
        communities. Supporting these projects allows you to compensate for your
        remaining, unavoidable emissions.
      </Typography>
      <Typography use="body1">
        Ecologi only supports projects certified by either the Gold Standard or
        Verra’s Verified Carbon Standard (VCS). Additional certifications for
        VCS certified projects indicate their contribution to sustainable
        development and the co-benefits they offer to local people and nature,
        such as the Climate, Community and Biodiversity Standard (CCBS) and the
        Sustainable Development Verified Impact Standard (SD VISta).
      </Typography>
      <Typography use="body1">
        We also use third-party platforms, including carbon intelligence
        platform Sylvera, to ensure the projects our community supports are of
        the highest standard.
      </Typography>
    </>
  ),
  [ImpactType.CARBON_REMOVAL]: () => (
    <>
      <Typography use="body1">
        One of the leading drivers of climate change is the increasing
        concentration of CO2 in our atmosphere as a result of human activity. To
        prevent climate breakdown, the concentration of CO2 in our atmosphere
        needs to stabilise at a level which would produce no more than 2°C of
        warming compared to pre-industrial levels.
      </Typography>
      <Typography use="body1">
        Under projected global warming pathways modelled by the IPCC, there is a
        role for removing carbon from the atmosphere, to complement significant
        global emissions reductions. We therefore can reduce the rate at which
        the atmospheric CO2 concentration is increasing by reducing emissions
        and by directly removing CO2 from the atmosphere.
      </Typography>
      <Typography use="body1">
        Carbon removal credits are carbon credits issued to projects which
        remove CO2e from the atmosphere. At Ecologi, we select carbon removal
        projects which have the best chance of permanent carbon removal and
        storage, and are accredited to leading standards. We support carbon
        removal through select methodologies administered by the Gold Standard,
        Verified Carbon Standard, and Puro Standard. Our team works hard to
        select projects which also have additional co-benefits, like benefits to
        local communities and to biodiversity.
      </Typography>
    </>
  ),
  [ImpactType.HABITAT_RESTORATION]: () => (
    <>
      <Typography use="body1">
        {`As habitats become degraded, their ability to support species declines.
        15% of the 8,431 species that have been assessed against the IUCN
        Regional Red List criteria are classified as being threatened and
        therefore at risk of extinction in the UK. `}
        <DarkLinkText
          as="a"
          use="inlineLink"
          target="_blank"
          href="https://nbn.org.uk/wp-content/uploads/2019/09/State-of-Nature-2019-UK-full-report.pdf"
          rel="noopener noreferrer"
        >
          43% of bird species and 26% of terrestrial mammals are threatened
        </DarkLinkText>
        {`, including much-loved creatures such as `}
        <DarkLinkText
          as="a"
          use="inlineLink"
          target="_blank"
          href="https://www.mammal.org.uk/science-research/red-list/"
          rel="noopener noreferrer"
        >
          red squirrels and hedgehogs.
        </DarkLinkText>
      </Typography>
      <Typography use="body1">
        {`Biodiversity is under threat due to a lack of suitable habitat, `}
        <DarkLinkText
          as="a"
          use="inlineLink"
          target="_blank"
          href="https://api.warwickshire.gov.uk/documents/WCCC-863-794"
          rel="noopener noreferrer"
        >
          but time and again studies have shown
        </DarkLinkText>
        {` that, when we provide this desperately needed habitat, species can
        return once more to the places where they used to live. `}
      </Typography>
      <Typography use="body1">
        {`Creating and restoring habitats isn’t just important for plants and
        animals above the ground; this habitat restoration work is vital to keep
        soils healthy too. For example, the presence of native wildflowers
        brings much-needed stability to soils, preventing soil degradation and
        erosion due to weather. `}
        <DarkLinkText
          as="a"
          use="inlineLink"
          target="_blank"
          href="https://uksoils.org/why-soil"
          rel="noopener noreferrer"
        >
          Healthy soils can support much more life
        </DarkLinkText>
        {`, providing organisms with the nutrients and moisture they need to
        survive. Furthermore, healthy soils are a very important carbon store,
        keeping carbon locked away for hundreds or even thousands of years.`}
      </Typography>
    </>
  ),
};

export const TITLES: Record<
  Exclude<ImpactType, ImpactType.TREES>,
  (user: User, rows: number) => JSX.Element
> = {
  [ImpactType.CARBON_OFFSETS]: (user, rows) => {
    return (
      <>
        <strong>{user.groupName || user.fullName()}</strong>
        {user.isFamilyPlan ? " have " : " has "}
        supported the prevention of{" "}
        <strong>
          {user.totalNumberOfTonnesOffset().toLocaleString()} tCO
          <sub>2</sub>e
        </strong>{" "}
        from being emitted through{" "}
        <strong>
          {rows} verified carbon avoidance {pluralise(rows, "project", false)}.
        </strong>
      </>
    );
  },
  [ImpactType.CARBON_REMOVAL]: (user, rows) => {
    return (
      <>
        <strong>{user.groupName || user.fullName()}</strong>
        {user.isFamilyPlan ? " have " : " has "}
        so far funded the removal of{" "}
        <strong>
          {pluralise(user.totalCarbonRemovedTonnes, "tonne")} of CO
          <sub>2</sub>e
        </strong>{" "}
        from the atmosphere across{" "}
        <strong>
          {rows} {pluralise(rows, "project", false)}.
        </strong>
      </>
    );
  },
  [ImpactType.HABITAT_RESTORATION]: (user, rows) => {
    return (
      <>
        <strong>{user.groupName || user.fullName()}</strong>
        {user.isFamilyPlan ? " have " : " has "}
        funded <strong>{user.totalHabitatMetersRestored}m²</strong> of habitat
        restoration across{" "}
        <strong>
          {rows} {pluralise(rows, "project", false)}.
        </strong>
      </>
    );
  },
};

export const NO_IMPACT_TITLES: Record<
  Exclude<ImpactType, ImpactType.TREES>,
  string
> = {
  [ImpactType.CARBON_OFFSETS]:
    "You have not yet funded any verified carbon avoidance projects.",
  [ImpactType.CARBON_REMOVAL]:
    "You have not yet removed any carbon via carbon removal projects.",
  [ImpactType.HABITAT_RESTORATION]:
    "You have not yet funded any habitat restoration.",
};
